import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ShieldCheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
    decideCustomerRiskApprovalApi,
    getCustomerRiskApprovalQueueApi,
} from '../../services/axios/AuthApis/RisksManagementApis';

const CustomerRiskApprovals = () => {
    const [searchParams] = useSearchParams();
    const [risks, setRisks] = useState([]);
    const [selectedRiskId, setSelectedRiskId] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const selectedRisk = risks.find((risk) => risk.id === selectedRiskId);

    const loadApprovals = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getCustomerRiskApprovalQueueApi();
            if (!response.success) {
                throw new Error(response.message || 'Could not load pending customer risks.');
            }
            const queue = response.data || [];
            setRisks(queue);
            const deepLinkedRisk = searchParams.get('risk_id');
            setSelectedRiskId(
                queue.some((risk) => risk.id === deepLinkedRisk)
                    ? deepLinkedRisk
                    : (queue[0]?.id || ''),
            );
        } catch (requestError) {
            console.error('Could not load customer risk approvals:', requestError);
            setError(requestError.response?.data?.message || requestError.message || 'Could not load pending customer risks.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadApprovals();
    }, [searchParams]);

    const submitDecision = async (decision) => {
        if (!selectedRisk || processing) return;
        setProcessing(true);
        setError('');
        setNotice('');
        try {
            const response = await decideCustomerRiskApprovalApi(
                selectedRisk.id,
                decision,
                rejectionReason,
            );
            if (!response.success) {
                throw new Error(response.message || 'The approval decision could not be saved.');
            }
            setRisks((previous) => previous.filter((risk) => risk.id !== selectedRisk.id));
            setSelectedRiskId('');
            setRejectionReason('');
            setNotice(response.message || `Risk ${decision}.`);
        } catch (requestError) {
            console.error(`Could not ${decision} customer risk:`, requestError);
            setError(requestError.response?.data?.message || requestError.message || 'The approval decision could not be saved.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <section className="space-y-5" aria-labelledby="customer-risk-approvals-title">
            <header>
                <h2 id="customer-risk-approvals-title" className="flex items-center gap-2 text-xl font-bold text-gray-900">
                    <ShieldCheckIcon className="h-6 w-6 text-primary-600" />
                    Customer Risk Approvals
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                    Review customer-created risks before they enter the approved risk catalogue.
                </p>
            </header>

            {notice && (
                <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    {notice}
                </div>
            )}
            {error && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                    Loading pending customer risks…
                </div>
            ) : risks.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                    <CheckCircleIcon className="mx-auto h-10 w-10 text-green-500" />
                    <p className="mt-3 font-medium text-gray-900">No risks awaiting approval</p>
                    <p className="mt-1 text-sm text-gray-500">New customer submissions will appear here.</p>
                </div>
            ) : (
                <div className="grid gap-5 lg:grid-cols-[minmax(240px,0.8fr)_minmax(0,1.5fr)]">
                    <nav aria-label="Pending customer risks" className="space-y-2">
                        {risks.map((risk) => (
                            <button
                                type="button"
                                key={risk.id}
                                onClick={() => {
                                    setSelectedRiskId(risk.id);
                                    setRejectionReason('');
                                }}
                                aria-current={risk.id === selectedRiskId ? 'true' : undefined}
                                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                                    risk.id === selectedRiskId
                                        ? 'border-primary-400 bg-primary-50'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                }`}
                            >
                                <span className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-gray-900">{risk.risk_id}</span>
                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Pending</span>
                                </span>
                                <span className="mt-1 block line-clamp-2 text-sm text-gray-600">{risk.risk_description}</span>
                                <span className="mt-2 block text-xs text-gray-500">{risk.customer_name || risk.customer_id}</span>
                            </button>
                        ))}
                    </nav>

                    {selectedRisk && (
                        <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Awaiting platform approval</p>
                                    <h3 className="mt-1 text-lg font-bold text-gray-900">{selectedRisk.risk_id}</h3>
                                </div>
                                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                    {selectedRisk.category} / {selectedRisk.risk_sub_category}
                                </span>
                            </div>

                            <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                                <div className="sm:col-span-2">
                                    <dt className="text-xs font-semibold uppercase text-gray-500">Risk description</dt>
                                    <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{selectedRisk.risk_description}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold uppercase text-gray-500">Customer</dt>
                                    <dd className="mt-1 text-sm text-gray-800">{selectedRisk.customer_name || selectedRisk.customer_id}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold uppercase text-gray-500">Created by</dt>
                                    <dd className="mt-1 text-sm text-gray-800">{selectedRisk.created_by || 'Unknown'}</dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-xs font-semibold uppercase text-gray-500">Control reference</dt>
                                    <dd className="mt-1 text-sm text-gray-800">{selectedRisk.framework_control || 'No control reference'}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold uppercase text-gray-500">Submitted</dt>
                                    <dd className="mt-1 text-sm text-gray-800">
                                        {selectedRisk.created_at ? new Date(selectedRisk.created_at).toLocaleString() : '—'}
                                    </dd>
                                </div>
                            </dl>

                            <label htmlFor="risk-rejection-reason" className="mt-5 block text-sm font-medium text-gray-700">
                                Decline reason <span className="font-normal text-gray-500">(optional)</span>
                            </label>
                            <textarea
                                id="risk-rejection-reason"
                                value={rejectionReason}
                                onChange={(event) => setRejectionReason(event.target.value)}
                                rows={3}
                                maxLength={1000}
                                className="mt-1 w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />

                            <div className="mt-5 flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4">
                                <button
                                    type="button"
                                    onClick={() => submitDecision('rejected')}
                                    disabled={processing}
                                    className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <XCircleIcon className="h-4 w-4" />
                                    {processing ? 'Saving…' : 'Decline'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => submitDecision('approved')}
                                    disabled={processing}
                                    className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <CheckCircleIcon className="h-4 w-4" />
                                    {processing ? 'Saving…' : 'Approve'}
                                </button>
                            </div>
                        </article>
                    )}
                </div>
            )}
        </section>
    );
};

export default CustomerRiskApprovals;